import React from 'react';
import { flagSummary, isLineFlagged } from '../constants/notifyReasons';

/**
 * NOTIFY USER — the mark on a flagged order LINE.
 *
 * Product owner, 2026-08-11: *"Highlight the out of stock item and send a
 * notification"*. The requirement behind it is that the state is visible ON THE
 * ORDER, not only in a notification that scrolls away.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A SHARED COMPONENT FOR SOMETHING THIS SMALL
 * ─────────────────────────────────────────────────────────────────────────────
 * The read-only line-item table is HAND-COPIED THREE TIMES — `DepartmentOrders
 * .js`, `Approvals.js` and `MyOrders.js` each render their own desktop table AND
 * their own mobile card — and the editor renders a fourth per-item row inside
 * `OrderForm.js`. Marking a flagged line therefore touches FOUR surfaces, and
 * four hand-copies of a pill is exactly what the Code Unification Principle
 * exists to stop. Extracting the whole table is too big for this change and is
 * not attempted; extracting the MARK is one file and four imports.
 *
 * 🔴 **`MyOrders.js` GETS THE MARK AND NO MENU, AND THE MARK THERE IS
 * LOAD-BEARING RATHER THAN NICE-TO-HAVE.** The requester cannot notify anybody
 * (they are the person being notified), so no actions menu belongs on that page
 * — but My Orders is the ONE page they actually use, so a notification pointing
 * at a line that is unmarked there is the half-a-feature trap: the ping says
 * "out of stock" and the order shows nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🎨 COLOUR — CHECKED AGAINST THE CASCADE, NOT JUST AGAINST CONTRAST
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ **`bg-amber-50` WOULD BE INERT IN DARK MODE AND IS DELIBERATELY NOT USED.**
 * `client/src/index.css:436` carries
 * `html.dark .bg-amber-50 { background-color: rgba(245,158,11,0.1) !important }`,
 * and an `!important` global beats a `dark:` utility outright — so the obvious
 * `bg-amber-50 dark:bg-amber-900/30` pair compiles, reads correctly, and renders
 * the wrong colour. Measured against that override table (which covers
 * `bg-{blue,red,yellow,orange,purple,indigo}-100`, `bg-{red,yellow,amber,blue}-50`
 * and `bg-yellow-200`): **`bg-amber-100` is NOT in it**, so its `dark:` partner
 * is live. That is why the tint is `-100` and not `-50`.
 *
 * ♿ **NEVER COLOUR ALONE.** The row tint is accompanied by a pill carrying the
 * reason TEXT, so WCAG 1.4.11's 3:1 non-text minimum does not apply to a sole
 * carrier — the `InactiveBadge` / `DueIndicator` device this repo already uses.
 * Deleting the pill and keeping the tint would PROMOTE the tint to sole carrier
 * and silently raise the bar on it; see CLAUDE.md's "deleting one of two
 * redundant signals" rule.
 *
 * ⚠️ jsdom applies no stylesheet, so tests here can only pin the class STRINGS.
 * **The rendered colours are a BROWSER check** and are owed one before release.
 */

/**
 * The row-level tint for a flagged line.
 *
 * 🪤 Exported as a STRING for the callers to compose into their own `<tr>` /
 * card `className`, rather than as a wrapper component: three of the four call
 * sites are table rows, and a `<tr>` cannot be wrapped in a `<div>`.
 */
export const FLAGGED_ROW_CLASS = 'bg-amber-100 dark:bg-amber-900/30';

/**
 * The pill. Renders NOTHING for an unflagged line, so every caller can render it
 * unconditionally and no caller repeats the predicate.
 *
 * @param {object} props.item        the order item row
 * @param {string} [props.testId]
 */
export default function OrderLineItemFlag({ item, testId, className = '' }) {
  if (!isLineFlagged(item)) return null;

  const summary = flagSummary(item);
  const note = (item && typeof item.notify_note === 'string' && item.notify_note.trim()) || '';

  return (
    <span
      // `className` (optional, default '' → the class string is unchanged) lets
      // `OrderForm` place the pill in its phone item grid.
      className={`ml-1 inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-200${className ? ` ${className}` : ''}`}
      data-testid={testId}
      // The note is the buyer's own sentence and belongs where there is room for
      // it. `title` (never `''`, which renders an empty popup box) is the
      // convention `buildOrderDateTooltip` set.
      title={note || undefined}
    >
      {summary}
    </span>
  );
}
